/**
 * Theme registry.
 *
 * A theme is a self-contained visual package under `src/themes/<name>/`:
 *
 *     <name>/
 *       theme.js      manifest: id, label, which stylesheets to load
 *       tokens.css    THE ONLY place raw colours / sizes / fonts are written
 *       skin.css      how the semantic classes in src/ui look, using tokens
 *
 * Nothing outside `src/themes/` may contain a hex colour, a px font-size or a
 * font-family. Application code refers only to semantic class names (`ui-btn`,
 * `ui-card`) and CSS custom properties (`var(--text)`), so replacing the whole
 * look means adding a directory here and changing the active id — no component
 * is touched.
 *
 * 主题注册表。
 *
 * 一个主题是 src/themes/<name>/ 下自包含的视觉包：
 *     <name>/
 *       theme.js      清单：id、名称、需要加载哪些样式表
 *       tokens.css    唯一允许写死颜色 / 尺寸 / 字体的地方
 *       skin.css      src/ui 里的语义类长什么样，只使用 tokens
 *
 * src/themes/ 之外的任何文件都不允许出现十六进制颜色、px 字号或 font-family。
 * 业务代码只引用语义类名（ui-btn、ui-card）和 CSS 变量（var(--text)），因此
 * 整体换皮 = 在这里加一个目录 + 改一下当前主题 id，不需要动任何组件。
 */

import bigtech from './bigtech/theme.js'

/**
 * All registered themes, keyed by id.
 *
 * To add one: create `src/themes/<id>/`, import its manifest here, add it to
 * this object. Nothing else in the codebase needs to change.
 *
 * 已注册的全部主题，按 id 索引。新增方式：建 src/themes/<id>/ 目录，在这里 import
 * 它的清单并加进本对象。代码库其他地方无需改动。
 */
export const THEMES = {
  [bigtech.id]: bigtech,
}

/** Theme used when nothing is stored or the stored id is unknown. 默认主题。 */
export const DEFAULT_THEME_ID = bigtech.id

/**
 * Resolve a theme id to its manifest, falling back to the default.
 *
 * 把主题 id 解析为清单，未知 id 回退到默认主题。
 *
 * @param {string} id Theme id / 主题 id。
 * @returns {object} Theme manifest / 主题清单。
 */
export function resolveTheme(id) {
  return THEMES[id] || THEMES[DEFAULT_THEME_ID]
}

/**
 * List themes for a picker UI.
 *
 * 列出主题，供选择器使用。
 *
 * @returns {Array<{id: string, labelZh: string, labelEn: string}>} Themes / 主题列表。
 */
export function listThemes() {
  return Object.values(THEMES).map(({ id, labelZh, labelEn, descriptionZh, descriptionEn }) => ({
    id,
    labelZh,
    labelEn,
    descriptionZh,
    descriptionEn,
  }))
}
